/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import { View, Text, ScrollView, Platform } from "react-native";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { pruneExpiredBanners } from "../../store/slices/adminSlice";
import lightStyles, { createHomeScreenThemeStyles } from "./HomeScreen.style";
import {
  safetyTopics,
  upcomingEvents,
  externalLinks,
  defaultCiData,
} from "./staticData";
import { logger } from "../../utils/logger";
import { TEXT } from "../../constants";
// Import custom hook for data fetching
import useHomeScreenData from "./hooks/useHomeScreenData";

// Import components
import MetricsSection from "./components/MetricsSection";
import CIActiveItems from "./components/CIActiveItems";
import SafetySpotlight from "./components/SafetySpotlight";
import UpcomingEvents from "./components/UpcomingEvents";
import UpcomingEventsOverlay from "./components/UpcomingEventsOverlay";
import ExternalLinks from "./components/ExternalLinks";
import ExternalLinksOverlay from "./components/ExternalLinksOverlay";
import ChangeLog from "./components/ChangeLog";
import MetricsOverlay from "./components/MetricsOverlay";
import CIItemsOverlay from "./components/CIItemsOverlay";
import SafetyTopicsOverlay from "./components/SafetyTopicsOverlay";
import { useTheme } from "../../theme";

const HomeScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors, isDark), [colors, isDark]);

  // Refresh banner statuses and prune expired banners (>24h old) on every homepage visit
  useEffect(() => {
    dispatch(pruneExpiredBanners());
  }, [dispatch]);

  // Use our custom hook to handle data fetching
  const {
    ciDashboardData,
    lhExecutionData,
    trainingEssentialsData,
    eventsData,
    externalLinksData,
    changeLogData,
    announcementBannerData,
    loading,
  } = useHomeScreenData();
  // Use ciDashboardData from Redux store instead of static data
  const ciData = ciDashboardData || defaultCiData;
  
  // Use dynamic SharePoint content data, avoid static fallbacks during loading
  const dynamicSafetyTopics = loading.trainingEssentials ? [] : (trainingEssentialsData?.data || [])
    .filter((item: any) => item.status === 'Active')
    .sort((a: any, b: any) => {
      const orderDiff = (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      if (orderDiff !== 0) return orderDiff;
      const startDiff = (a.startDate ?? '').localeCompare(b.startDate ?? '');
      if (startDiff !== 0) return startDiff;
      return (a.endDate ?? '').localeCompare(b.endDate ?? '');
    });
  const dynamicUpcomingEvents = loading.events ? [] : (eventsData?.data || []);
  const dynamicExternalLinks = loading.externalLinks ? [] : (externalLinksData?.data || []);

  // Format metrics data for the overlay using lhExecutionData
  const formatMetricsFromResponse = () => {
    if (!lhExecutionData) {
      logger.log("No LH Execution data available");
      return [];
    }

    const metrics = [];
    const toPercent = (val: number | undefined) =>
      typeof val === "number" ? `${Math.round(val * 100)}%` : "0%";

    // Format location data
    if (lhExecutionData.location) {
      metrics.push({
        region:
          lhExecutionData.location.locationName || TEXT.COMMON.CURRENT_LOCATION,
        pulseCheck: toPercent(lhExecutionData.location.pulseChecksPercentage),
        observation: toPercent(lhExecutionData.location.observationsPercentage),
        shift: toPercent(lhExecutionData.location.shiftHandoversPercentage),
        leadershipWalk: toPercent(
          lhExecutionData.location.leadershipWalksPercentage
        ),
        sparcWalk: toPercent(lhExecutionData.location.sparcWalksPercentage),
      });
    }

    // Format market data (Pacific Northwest)
    if (lhExecutionData.market) {
      metrics.push({
        region: lhExecutionData.market.marketName || TEXT.COMMON.DIVISION,
        pulseCheck: toPercent(lhExecutionData.market.pulseChecksPercentage),
        observation: toPercent(lhExecutionData.market.observationsPercentage),
        shift: toPercent(lhExecutionData.market.shiftHandoversPercentage),
        leadershipWalk: toPercent(
          lhExecutionData.market.leadershipWalksPercentage
        ),
        sparcWalk: toPercent(lhExecutionData.market.sparcWalksPercentage),
      });
    }

    // Format region data (West)
    if (lhExecutionData.region) {
      metrics.push({
        region: lhExecutionData.region.regionName || TEXT.COMMON.REGION,
        pulseCheck: toPercent(lhExecutionData.region.pulseChecksPercentage),
        observation: toPercent(lhExecutionData.region.observationsPercentage),
        shift: toPercent(lhExecutionData.region.shiftHandoversPercentage),
        leadershipWalk: toPercent(
          lhExecutionData.region.leadershipWalksPercentage
        ),
        sparcWalk: toPercent(lhExecutionData.region.sparcWalksPercentage),
      });
    }

    return metrics;
  };

  // Get metrics data for the overlay - memoized to prevent unnecessary re-computations
  const getMetricsData = useMemo(() => {
    return formatMetricsFromResponse();
  }, [lhExecutionData]);

  // State management
  const [safetyOverlayVisible, setSafetyOverlayVisible] = useState(false);
  const [upcomingEventsOverlayVisible, setUpcomingEventsOverlayVisible] = useState(false);
  const [externalLinksOverlayVisible, setExternalLinksOverlayVisible] = useState(false);
  const [ciOverlayVisible, setCiOverlayVisible] = useState(false);
  const [metricsOverlayVisible, setMetricsOverlayVisible] = useState(false);
  const [selectedCiStatus, setSelectedCiStatus] = useState<
    "On Track" | "Off Track" | "All"
  >("Off Track");

  // TimeFrame dropdown state
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<string | null>(
    "CURRENT"
  );
  // Track if dropdown has changed and new metrics need to be generated
  const [metricsDirty, setMetricsDirty] = useState(false);
  // Track the last successfully loaded timeframe
  const [lastLoadedTimeFrame, setLastLoadedTimeFrame] = useState<string | null>(
    "CURRENT"
  );

  return (
    <View style={[lightStyles.screenRoot, themeStyles.screenRoot]}>
      <View style={[lightStyles.container, themeStyles.container]}>
      {/* Main Content */}
      <ScrollView style={lightStyles.content}>
        {/* Dashboard Content */}
        <View style={lightStyles.gridContainer}>
          {/* Top Section */}
          <View style={lightStyles.topSection}>
            <View
              style={[
                lightStyles.topRow,
                Platform.OS !== "web" && lightStyles.topRowMobile,
              ]}
            >
              {/* Metrics Card */}
              <MetricsSection
                selectedTimeFrame={selectedTimeFrame}
                setSelectedTimeFrame={setSelectedTimeFrame}
                metricsDirty={metricsDirty}
                setMetricsDirty={setMetricsDirty}
                lastLoadedTimeFrame={lastLoadedTimeFrame}
                setLastLoadedTimeFrame={setLastLoadedTimeFrame}
                setMetricsOverlayVisible={setMetricsOverlayVisible}
              />

              {/* CI Active Items */}
              <CIActiveItems
                setSelectedCiStatus={setSelectedCiStatus}
                setCiOverlayVisible={setCiOverlayVisible}
              />
            </View>
          </View>
          {/* Bottom Section */}
          <View style={lightStyles.bottomSection}>
            {/* Safety Spotlight */}
            <SafetySpotlight
              safetyTopics={dynamicSafetyTopics}
              setSafetyOverlayVisible={setSafetyOverlayVisible}
              bannerData={announcementBannerData}
            />
            {/* Upcoming Events */}
            <UpcomingEvents
             
              upcomingEvents={dynamicUpcomingEvents}
              setUpcomingEventsOverlayVisible={setUpcomingEventsOverlayVisible}
           
            />
            {/* External Links */}
            <ExternalLinks
             
              externalLinks={dynamicExternalLinks}
              setExternalLinksOverlayVisible={setExternalLinksOverlayVisible}
           
            />
          </View>
        </View>
        {/* Change Log */}
        <ChangeLog changeLogData={changeLogData} />
      </ScrollView>
      </View>
      {/* Only the necessary overlays for core functionality */}
      <SafetyTopicsOverlay
        visible={safetyOverlayVisible}
        onDismiss={() => setSafetyOverlayVisible(false)}
        safetyTopics={dynamicSafetyTopics}
      />
      <UpcomingEventsOverlay
        visible={upcomingEventsOverlayVisible}
        onDismiss={() => setUpcomingEventsOverlayVisible(false)}
        upcomingEvents={dynamicUpcomingEvents}
      />
      <ExternalLinksOverlay
        visible={externalLinksOverlayVisible}
        onDismiss={() => setExternalLinksOverlayVisible(false)}
        externalLinks={dynamicExternalLinks}
      />
      <CIItemsOverlay
        visible={ciOverlayVisible}
        onDismiss={() => setCiOverlayVisible(false)}
        ciData={ciData}
        selectedCiStatus={selectedCiStatus}
      />
      <MetricsOverlay
        visible={metricsOverlayVisible}
        onDismiss={() => setMetricsOverlayVisible(false)}
        metricsData={getMetricsData}
      />
    </View>
  );
};

export default HomeScreen;
